import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getItem } from "../../../../../utils/localStorage";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import {
  getBankDetailsURL,
  fetchFacilityPaymentDetails as FETCH_FACILITY_PAYMENT_DETAILS_URL,
} from "../../../../../utils/urls";

const PaymentMethodsTab = ({
  formData,
  updateFormData,
  onNext,
  onPrevious,
  validation
}) => {
  // Always call hooks first, before any early returns
  const [bankDetails, setBankDetails] = useState([]);
  const [, setLoadingBankDetails] = useState(false);

  // Facility M-Pesa/paybill configuration — same source SettingsTab uses for water bills
  const [facilityPaymentDetails, setFacilityPaymentDetails] = useState([]);
  const [, setLoadingPaymentDetails] = useState(false);

  // Fetch bank details and paybill configs on component mount
  useEffect(() => {
    fetchBankDetails();
    fetchFacilityPaymentDetails();
  }, []);

  // Guard against undefined formData
  if (!formData || !formData.financialTerms) {
    return (
      <div className="p-4 text-center">
        <i className="ti ti-loader-2 text-4xl text-primary animate-spin"></i>
        <p className="mt-2 text-600">Loading...</p>
      </div>
    );
  }

  const { financialTerms } = formData;

  const fetchBankDetails = async () => {
    try {
      setLoadingBankDetails(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("No facility selected. Please select a facility first.", "error");
        return;
      }

      const response = await makeRequest2(`${getBankDetailsURL}/${facilityId}`, 'GET');

      if (response && response.success) {
        let bankDetailsData = [];

        // Handle different possible response structures
        if (Array.isArray(response.data)) {
          bankDetailsData = response.data;
        } else if (response.data?.bankDetails) {
          bankDetailsData = response.data.bankDetails;
        } else if (response.data?.data) {
          bankDetailsData = response.data.data;
        }

        setBankDetails(bankDetailsData);
      } else {
        console.error("Failed to fetch bank details:", response);
        toastify("Failed to load bank details", "error");
      }
    } catch (error) {
      console.error('Error fetching bank details:', error);
      toastify("Failed to load bank details", "error");
    } finally {
      setLoadingBankDetails(false);
    }
  };

  // Same shape/logic as SettingsTab.fetchFacilityPaymentDetails() so leases and
  // water invoices resolve mobile/paybill payment methods against the same data.
  const fetchFacilityPaymentDetails = async () => {
    try {
      setLoadingPaymentDetails(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("No facility selected. Please select a facility first.", "error");
        return;
      }

      const response = await makeRequest2(
        `${FETCH_FACILITY_PAYMENT_DETAILS_URL}/${facilityId}`,
        "GET"
      );

      if (response && response.success && response.data) {
        let details = [];
        if (Array.isArray(response.data)) {
          details = response.data;
        } else if (typeof response.data === 'object' && response.data !== null) {
          if (response.data.data && Array.isArray(response.data.data)) {
            details = response.data.data;
          } else {
            details = [response.data];
          }
        }
        setFacilityPaymentDetails(details);
      } else {
        setFacilityPaymentDetails([]);
      }
    } catch (error) {
      console.error('Error fetching facility payment details:', error);
      toastify("Failed to load mobile payment details", "error");
      setFacilityPaymentDetails([]);
    } finally {
      setLoadingPaymentDetails(false);
    }
  };

  const handleAddPaymentMethod = () => {
    const updatedMethods = [
      ...financialTerms.paymentMethods,
      {
        type: "",
        details: {},
        isPrimary: financialTerms.paymentMethods.length === 0
      }
    ];
    updateFormData.updateFinancialTerms({ paymentMethods: updatedMethods });
  };

  const handleRemovePaymentMethod = (index) => {
    const updatedMethods = financialTerms.paymentMethods.filter((_, i) => i !== index);
    // If we removed the primary method, make the first one primary
    if (updatedMethods.length > 0 && !updatedMethods.some(m => m.isPrimary)) {
      updatedMethods[0].isPrimary = true;
    }
    updateFormData.updateFinancialTerms({ paymentMethods: updatedMethods });
  };

  const handleMethodChange = (index, field, value) => {
    const updatedMethods = [...financialTerms.paymentMethods];
    if (field === 'type') {
      updatedMethods[index] = {
        ...updatedMethods[index],
        type: value,
        details: {}, // Reset details when type changes
        bankDetailsId: undefined,
        paymentDetailsId: undefined
      };
    } else if (field === 'bankDetailsId') {
      // When selecting a bank from dropdown, store only the reference ID
      updatedMethods[index] = {
        ...updatedMethods[index],
        bankDetailsId: value,
        details: {
          ...updatedMethods[index].details
        }
      };
    } else if (field === 'paymentDetailsId') {
      // When selecting a paybill config from dropdown, store only the reference ID —
      // same pattern as bankDetailsId, resolved server-side against facilityPaymentDetails
      const selected = facilityPaymentDetails.find(p => p._id === value);
      updatedMethods[index] = {
        ...updatedMethods[index],
        paymentDetailsId: value,
        details: {
          ...updatedMethods[index].details,
          businessNumber: selected?.shortCode || ''
        }
      };
    } else {
      updatedMethods[index] = {
        ...updatedMethods[index],
        details: {
          ...updatedMethods[index].details,
          [field]: value
        }
      };
    }
    updateFormData.updateFinancialTerms({ paymentMethods: updatedMethods });
  };

  const handleSetPrimary = (index) => {
    const updatedMethods = financialTerms.paymentMethods.map((method, i) => ({
      ...method,
      isPrimary: i === index,
    }));
    updateFormData.updateFinancialTerms({ paymentMethods: updatedMethods });
  };

  const handleNext = () => {
    const validationResult = validation.validatePaymentMethods();

    if (!validationResult.isValid) {
      validationResult.errors.forEach(error => {
        toastify(error, "error");
      });
      return;
    }

    onNext();
  };

  const paymentTypeOptions = [
    { label: "Bank Transfer", value: "Bank Transfer" },
    { label: "Mobile Money", value: "Mobile Money" },
    { label: "Cash", value: "Cash" },
    { label: "Cheque", value: "Cheque" }
  ];

  return (
    <div className="row">
      {/* Payment Methods */}
      <div className="col-12 mb-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="text-primary m-0">Payment Methods</h6>
          <Button
            label="Add Payment Method"
            icon="ti ti-plus"
            className="p-button-outlined p-button-sm"
            onClick={handleAddPaymentMethod}
          />
        </div>

        {financialTerms.paymentMethods && financialTerms.paymentMethods.length > 0 ? (
          financialTerms.paymentMethods.map((method, index) => (
            <div key={index} className="card mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="m-0">Payment Method {index + 1}</h6>
                  <div className="d-flex align-items-center">
                    {!method.isPrimary && (
                      <Button
                        label="Set as Primary"
                        className="p-button-text p-button-sm mr-2"
                        onClick={() => handleSetPrimary(index)}
                      />
                    )}
                    {method.isPrimary && (
                      <span className="badge badge-primary mr-2">
                        Primary
                      </span>
                    )}
                    {financialTerms.paymentMethods.length > 1 && (
                      <Button
                        icon="ti ti-trash"
                        className="p-button-danger p-button-text p-button-sm"
                        onClick={() => handleRemovePaymentMethod(index)}
                      />
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label>Payment Type <span className="text-danger">*</span></label>
                      <Dropdown
                        value={method.type}
                        options={paymentTypeOptions}
                        onChange={(e) => handleMethodChange(index, 'type', e.value)}
                        placeholder="Select payment type"
                        className="w-100"
                      />
                    </div>
                  </div>

                  {/* Bank Transfer Details */}
                  {method.type === "Bank Transfer" && (
                    <div className="col-12 mb-3">
                      <div className="form-group">
                        <label>
                          Select Bank Account <span className="text-danger">*</span>
                        </label>
                        {bankDetails.length > 0 ? (
                          <Dropdown
                            value={method.bankDetailsId || ""}
                            options={bankDetails.map(bank => ({
                              label: `${bank.bankName} - ${bank.accountName} (${bank.accountNumber})`,
                              value: bank._id
                            }))}
                            onChange={(e) => handleMethodChange(index, 'bankDetailsId', e.value)}
                            placeholder="Select bank account"
                            className="w-100"
                            showClear
                            filter
                          />
                        ) : (
                          <div className="alert alert-info d-flex justify-content-between align-items-center">
                            <span>No bank accounts available. Please add a bank account first.</span>
                            <Link
                              to="/facility/settings"
                              className="btn btn-primary btn-sm"
                            >
                              <i className="ti ti-plus mr-1"></i>
                              Add Bank Account
                            </Link>
                          </div>
                        )}
                        {method.bankDetailsId && (
                          <small className="text-success d-block mt-2">
                            <i className="ti ti-check-circle mr-1"></i>
                            Bank account selected
                          </small>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Mobile Money Details — pulled from the facility's paybill configs,
                      same source/endpoint as the water meter Settings tab */}
                  {method.type === "Mobile Money" && (
                    <div className="col-12 mb-3">
                      <div className="form-group">
                        <label>Select Paybill Configuration <span className="text-danger">*</span></label>
                        {facilityPaymentDetails.length > 0 ? (
                          <Dropdown
                            value={method.paymentDetailsId || ""}
                            options={facilityPaymentDetails.map(payment => ({
                              label: `Paybill: ${payment.shortCode} - ${payment.module}`,
                              value: payment._id
                            }))}
                            onChange={(e) => handleMethodChange(index, 'paymentDetailsId', e.value)}
                            placeholder="Select paybill configuration"
                            className="w-100"
                            showClear
                            filter
                          />
                        ) : (
                          <div className="alert alert-info d-flex justify-content-between align-items-center">
                            <span>No M-Pesa paybill configuration found. Please add one first.</span>
                            <Link
                              to="/facility/settings"
                              className="btn btn-primary btn-sm"
                            >
                              <i className="ti ti-plus mr-1"></i>
                              Add Paybill
                            </Link>
                          </div>
                        )}
                        {method.paymentDetailsId && (
                          <small className="text-success d-block mt-2">
                            <i className="ti ti-check-circle mr-1"></i>
                            Paybill configuration selected
                          </small>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cash Details */}
                  {method.type === "Cash" && (
                    <div className="col-12 mb-3">
                      <div className="form-group">
                        <label>Preferred Cash Collection Location</label>
                        <InputText
                          value={method.details?.preferredCashLocation || ""}
                          onChange={(e) => handleMethodChange(index, 'preferredCashLocation', e.target.value)}
                          placeholder="Enter preferred location for cash collection"
                          className="w-100"
                        />
                      </div>
                    </div>
                  )}

                  {/* Cheque Details */}
                  {method.type === "Cheque" && (
                    <>
                      <div className="col-md-6 mb-3">
                        <div className="form-group">
                          <label>Bank to Draft Cheque <span className="text-danger">*</span></label>
                          <InputText
                            value={method.details?.bankToDraft || ""}
                            onChange={(e) => handleMethodChange(index, 'bankToDraft', e.target.value)}
                            placeholder="Enter bank name"
                            className="w-100"
                          />
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <div className="form-group">
                          <label>Account Number</label>
                          <InputText
                            value={method.details?.chequeAccountNumber || ""}
                            onChange={(e) => handleMethodChange(index, 'chequeAccountNumber', e.target.value)}
                            placeholder="Enter account number"
                            className="w-100"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-4">
            <p className="text-muted">No payment methods added yet.</p>
            <Button
              label="Add First Payment Method"
              icon="ti ti-plus"
              className="p-button-outlined"
              onClick={handleAddPaymentMethod}
            />
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="col-12 mt-4">
        <div className="d-flex justify-content-between">
          <Button
            label="Previous: Financial Terms"
            icon="ti ti-arrow-left"
            className="p-button-secondary"
            onClick={onPrevious}
          />
          <Button
            label="Next: GL Accounts"
            icon="ti ti-arrow-right"
            iconPos="right"
            className="p-button-primary"
            onClick={handleNext}
            disabled={!financialTerms.paymentMethods || financialTerms.paymentMethods.length === 0}
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodsTab;